import { useState, useEffect, useContext } from 'react';
import UserStatusContext from '../contexts/UserStatusContext';

/**
 * Hook to manage and listen to user online status
 * @param {string} userId - Optional user ID to track specific user's status
 * @returns {Object} Status data and control functions
 */
export const useUserStatus = (userId = null) => {
  const [isOnline, setIsOnline] = useState(false);
  const [status, setStatus] = useState('offline');

  // Use useContext directly - it returns undefined if context not available
  const contextData = useContext(UserStatusContext);

  useEffect(() => {
    if (contextData && userId) {
      const online = contextData.isUserOnline(userId);
      const userStatus = contextData.getUserStatus(userId);

      setIsOnline(online);
      setStatus(userStatus);
    }
  }, [userId, contextData?.updateCounter, contextData]);

  // Return default values if context not available
  if (!contextData) {
    return {
      isOnline: false,
      status: 'offline',
      onlineUsers: new Set(),
      isInitialized: false,
      changeStatus: () => {},
      checkUserOnline: () => false,
      getUserStatus: () => 'offline',
      updateCounter: 0,
    };
  }

  return {
    isOnline,
    status,
    onlineUsers: contextData.onlineUsers,
    isInitialized: contextData.isInitialized,
    changeStatus: contextData.changeStatus,
    checkUserOnline: contextData.isUserOnline,
    getUserStatus: contextData.getUserStatus,
    updateCounter: contextData.updateCounter,
  };
};

export default useUserStatus;
